import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  QueryDefinition,
} from "@reduxjs/toolkit/dist/query";
import { PrefetchOptions } from "@reduxjs/toolkit/dist/query/core/module";
import { UseQuery } from "@reduxjs/toolkit/dist/query/react/buildHooks";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ApiRequest, ApiResponse } from "src/config/store/services/types";
import { pageLimit } from "./data-table";

type Direction = "asc" | "desc";

export type OrderBy = {
  direction: Direction;
  columnName: string;
};

type Prefetch = (
  arg: ApiRequest | undefined,
  options?: PrefetchOptions | undefined
) => void;

type TableParams = {
  page: string;
  sortDirection?: string;
  sortColumn?: string;
  search?: string;
};

type InitialParams = {
  page: number;
  sort?: OrderBy;
  search?: string;
};

type DataQuery<
  D extends Record<string, any>,
  T extends string,
  E extends string
> = UseQuery<
  QueryDefinition<
    ApiRequest | undefined,
    BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, {}, {}>,
    T,
    ApiResponse<D>,
    E
  >
>;

export const useDataTable = <
  D extends Record<string, any>,
  T extends string,
  E extends string
>(
  prefetchPage: Prefetch,
  dataQuery: DataQuery<D, T, E>
) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const params = useMemo(() => {
    const initialSort = {
      direction: searchParams.get("sortDirection") as Direction,
      columnName: searchParams.get("sortColumn") as string,
    };

    const initialSearch = searchParams.get("search") || undefined;

    const initialParams: InitialParams = {
      page: parseInt(searchParams.get("page") || "1"),
      sort: initialSort,
      search: initialSearch,
    };

    if (!initialSort.columnName) {
      delete initialParams.sort;
    }

    return initialParams;
  }, [searchParams]);

  const [sort, setSort] = useState<OrderBy>(
    params.sort || {
      direction: "asc",
      columnName: "",
    }
  );

  const [page, setPage] = useState(params.page || 1);

  const [search, setSearch] = useState(params.search || "");

  const { data, isFetching } = dataQuery({ page, sort, search });

  const allPages = useMemo(
    () => (data ? Math.ceil(data.count / pageLimit) : 0),
    [data]
  );

  const handleSortChange = (sort: OrderBy) => {
    setSort(sort);
  };

  const handlePageChange = (page: number) => {
    if (page < 1 || page > allPages) {
      return;
    }

    setPage(page);
  };

  const changeSearchParams = useCallback(() => {
    let params: TableParams = {
      page: page.toString(),
    };

    if (sort.columnName && sort.direction) {
      params = {
        ...params,
        sortDirection: sort?.direction || "",
        sortColumn: sort?.columnName || "",
      };
    }

    if (search) {
      params = {
        ...params,
        search: search,
      };
    }

    setSearchParams(new URLSearchParams(params).toString(), {
      replace: true,
    });
  }, [page, sort, search, setSearchParams]);

  const handlePrefetch = (page: number | null) => {
    if (page) {
      prefetchPage({ page, sort, search });
    }
  };

  const handleSearch = (value: string) => {
    setPage(1);
    setSearch(value);
  };

  useEffect(() => {
    changeSearchParams();
  }, [page, sort, search, changeSearchParams]);

  const onDelete = () => {
    if (allPages > 1) {
      const isLastPage = page === allPages;

      const isLastItemOnPage = data?.items.length === 1;

      if (isLastPage && isLastItemOnPage) {
        handlePageChange(page - 1);
      }
    }
  };

  return {
    page,
    sort,
    search,
    data,
    isFetching,
    handleSortChange,
    handlePageChange,
    handleSearch,
    handlePrefetch,
    onDelete,
  };
};
